import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import luckyDrawModel from "@/models/lucky-draw/lucky-draw";
import router from "@/router";
import store from "@/store";
import useFlatPickr from "@/utils/hooks/useFlatPickr";
import {
  onBeforeMount,
  reactive,
  ref,
  getCurrentInstance,
  computed,
} from "@vue/composition-api";

export default function useCampaignInfo() {
  const vm = getCurrentInstance().proxy;

  const {
    createCampaign,
    fetchCampaignList,
    fetchCampaignDetail,
    editCampaign,
    setCampaignStatus,
  } = luckyDrawModel();
  const {
    openDefaultErrorModal,
    openErrorModal,
    openSuccessModal,
  } = useValidationModal();

  const {
    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    resolveDateObject,
    resolveDate,
    resolveTime,
    resolveReverseDate,
  } = useFlatPickr();

  const formData = reactive({
    seq: 0,
    id: 0,
    name: "",
    campaignNameTh: "",
    campaignNameEn: "",
    pointUsed: null,
    redeemable: null,
    issueOverall: null,
    issuePerMonth: null,
    issuePerDay: null,
    issuePerCustomer: null,
    issuePerCustomerMonth: null,
    issuePerCustomerDay: null,

    status: false,
    issueLimit: false,
    mainImage: { file: null, url: null },
    subImage: { file: null, url: null },
    validFrom: {
      date: "",
      time: "",
    },
    validThrough: {
      date: "",
      time: "",
    },
    ruleTh: "",
    ruleEn: "",
  });

  const isEdit = ref(false);

  const fetchCampaignInfoFunc = () => {
    window.$loading(true);
    fetchCampaignDetail.payload.CampaignId = formData.id;

    store
      .dispatch("api/apiRequest", fetchCampaignDetail)
      .then((response) => {
        const respData = response.data.resp;
        if (Object.keys(respData).length > 0) {
          formData.id = respData.luckydraw_campaignid;
          formData.campaignNameTh = respData.campaign_name_th;
          formData.campaignNameEn = respData.campaign_name_en;
          formData.mainImage.url = respData.main_image_url;
          formData.subImage.url = respData.sub_image_url;
          formData.pointUsed = respData.redem_point;
          formData.redeemable = respData.amount;
          formData.ruleTh = respData.html_condition_th;
          formData.ruleEn = respData.html_condition_en;
          formData.validFrom.date = resolveDate(respData.valid_form);
          formData.validFrom.time = resolveTime(respData.valid_form);
          formData.validThrough.date = resolveDate(respData.valid_through);
          formData.validThrough.time = resolveTime(respData.valid_through);
          formData.status = respData.status == 1;
          formData.issueLimit = respData.is_limit;
          formData.issueOverall = respData.issue_overall;
          formData.issuePerMonth = respData.issue_per_month;
          formData.issuePerDay = respData.issue_per_day;
          formData.issuePerCustomer = respData.issue_customer_limit;
          formData.issuePerCustomerMonth =
            respData.issue_customer_limit_per_month;
          formData.issuePerCustomerDay = respData.issue_customer_limit_per_day;
        }
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const checkFormCreateOrEdit = () => {
    isEdit.value = false;
    if (router.currentRoute.params.campaignId) {
      isEdit.value = true;
      formData.id = router.currentRoute.params.campaignId;
    }
  };

  const resolveDateFormat = (dateStr) => {
    if (!dateStr) return;
    const dateSplit = dateStr.split("-");
    const date = dateSplit[0];
    const month = dateSplit[1];
    const year = dateSplit[2];

    return `${year}-${month}-${date}`;
  };

  const onSubmitForm = async () => {
    window.$loading(true);
    const payload = {
      CampaignId: formData.id,
      Name_TH: formData.campaignNameTh,
      Name_EN: formData.campaignNameEn,
      Main_Image: formData.mainImage.file,
      main_image_URL: formData.mainImage.url,
      Sub_Image: formData.subImage.file,
      Sub_Image_URL: formData.subImage.url,
      Point: formData.pointUsed,
      Quantity: formData.redeemable,
      Conditions_TH: formData.ruleTh,
      Conditions_EN: formData.ruleEn,
      Valid_Form:
        formData.validFrom.date && formData.validFrom.time
          ? `${resolveDateFormat(formData.validFrom.date)} ${formData.validFrom.time
          }`
          : null,
      Valid_Through:
        formData.validThrough.date && formData.validThrough.time
          ? `${resolveDateFormat(formData.validThrough.date)} ${formData.validThrough.time
          }`
          : null,
      IsLimit: formData.issueLimit,
      Issue_Overall: formData.issueLimit ? formData.issueOverall : 0,
      Issue_Per_Month: formData.issueLimit ? formData.issuePerMonth : 0,
      Issue_Per_Day: formData.issueLimit ? formData.issuePerDay : 0,
      Issue_Per_Customer: formData.issueLimit ? formData.issuePerCustomer : 0,
      Issue_Per_Customer_Month: formData.issueLimit
        ? formData.issuePerCustomerMonth
        : 0,
      Issue_Per_Customer_Day: formData.issueLimit
        ? formData.issuePerCustomerDay
        : 0,
      Status: formData.status == true ? 1 : 2,
    };

    if (formData.id || isEdit.value) {
      editCampaignLuckyDraw(payload);
    } else {
      delete payload.CampaignId;
      createCampaignLuckyDraw(payload);
    }
  };

  const createCampaignLuckyDraw = (payload) => {
    window.$loading(true);
    createCampaign.payload = payload;
    store
      .dispatch("api/apiFormRequest", createCampaign)
      .then((response) => {
        if (response.status == "success") {
          openSuccessModal("ทำรายการสำเร็จ !", null, null, null, () => {
            router.replace(`/lucky-draw/campaign/list`);
          });
        }
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const editCampaignLuckyDraw = (payload) => {
    window.$loading(true);
    editCampaign.payload = payload;

    store
      .dispatch("api/apiFormRequest", editCampaign)
      .then((response) => {
        if (response.status == "success") {
          openSuccessModal("ทำรายการสำเร็จ !", null, null, null, () => {
            router.replace(`/lucky-draw/campaign/list`);
          });
        }
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const onUploadMainImg = (image) => {
    formData.mainImage.file = image.file;
    formData.mainImage.url = image.url;
  };

  const onUploadSubImg = (image) => {
    formData.subImage.file = image.file;
    formData.subImage.url = image.url;
  };

  const onNumberInput = (ev, formDataValue) => {
    vm.$nextTick(() => {
      const inputNumber = ev.replace(/\D/g, "");
      formData[formDataValue] = inputNumber;
    });
  };

  const checkInput = computed(() => {
    return (
      formData.redeemable <= 0 ||
      parseInt(formData.issuePerMonth) > parseInt(formData.issueOverall) ||
      parseInt(formData.issuePerDay) > parseInt(formData.issuePerMonth) ||
      parseInt(formData.issuePerCustomer) > parseInt(formData.issueOverall) ||
      parseInt(formData.issuePerCustomerMonth) >
      parseInt(formData.issuePerCustomer) ||
      parseInt(formData.issuePerCustomerMonth) >
      parseInt(formData.issuePerMonth) ||
      parseInt(formData.issuePerCustomerDay) >
      parseInt(formData.issuePerCustomerMonth) ||
      parseInt(formData.issuePerCustomerDay) > parseInt(formData.issuePerDay)
    );
  });


  onBeforeMount(() => {
    checkFormCreateOrEdit();
    if (formData.id) {
      fetchCampaignInfoFunc();
    }
  });

  return {
    formData,
    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    onSubmitForm,
    onUploadMainImg,
    onUploadSubImg,
    onNumberInput,
    isEdit,
    checkInput,

  };
}
